import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import TopNav from "@/components/TopNav";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Checklist from "@/pages/Checklist";
import Reports from "@/pages/Reports";
import ReportDetail from "@/pages/ReportDetail";
import Issues from "@/pages/Issues";
import Admin from "@/pages/Admin";
import Schedules from "@/pages/Schedules";
import ScheduleBuilder from "@/pages/ScheduleBuilder";
import ScheduleDetail from "@/pages/ScheduleDetail";
import WeeklySchedule from "@/pages/WeeklySchedule";
import Requests from "@/pages/Requests";
import ShiftTemplates from "@/pages/ShiftTemplates";
import DisplayDashboard from "@/pages/DisplayDashboard";
import DisplayNew from "@/pages/DisplayNew";
import DisplayRun from "@/pages/DisplayRun";
import DisplayDetail from "@/pages/DisplayDetail";
import DisplayReports from "@/pages/DisplayReports";
import DisplayPhotos from "@/pages/DisplayPhotos";
import FloatLog from "@/pages/FloatLog";
import FloatRecord from "@/pages/FloatRecord";
import FloatDetail from "@/pages/FloatDetail";
import ChangePassword from "@/pages/ChangePassword";
import MyDisplayScores from "@/pages/MyDisplayScores";
import MyDisplayReports from "@/pages/MyDisplayReports";
import ZoneOwnerHistory from "@/pages/ZoneOwnerHistory";
import ReceivingList from "@/pages/ReceivingList";
import ReceivingNew from "@/pages/ReceivingNew";
import ReceivingDetail from "@/pages/ReceivingDetail";
import GlobalStandardsQueue from "@/pages/GlobalStandardsQueue";
import DamageList from "@/pages/DamageList";
import DamageNew from "@/pages/DamageNew";
import DamageDetail from "@/pages/DamageDetail";

const ADMIN_ROLES = ["admin"];
const BUILDER_ROLES = ["admin", "gm", "manager"];

function Layout({ children }) {
  return (
    <>
      <TopNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">{children}</main>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" theme="dark" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
            <Route path="/opening" element={<ProtectedRoute><Layout><Checklist type="opening" /></Layout></ProtectedRoute>} />
            <Route path="/closing" element={<ProtectedRoute><Layout><Checklist type="closing" /></Layout></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Layout><Reports /></Layout></ProtectedRoute>} />
            <Route path="/reports/:id" element={<ProtectedRoute><Layout><ReportDetail /></Layout></ProtectedRoute>} />
            <Route path="/issues" element={<ProtectedRoute><Layout><Issues /></Layout></ProtectedRoute>} />
            <Route path="/schedules" element={<ProtectedRoute feature="schedule"><Layout><Schedules /></Layout></ProtectedRoute>} />
            <Route path="/schedules/week" element={<ProtectedRoute feature="weekly"><Layout><WeeklySchedule /></Layout></ProtectedRoute>} />
            <Route path="/schedules/new" element={<ProtectedRoute roles={BUILDER_ROLES} feature="schedule"><Layout><ScheduleBuilder /></Layout></ProtectedRoute>} />
            <Route path="/schedules/:id" element={<ProtectedRoute feature="schedule"><Layout><ScheduleDetail /></Layout></ProtectedRoute>} />
            <Route path="/schedules/:id/edit" element={<ProtectedRoute roles={BUILDER_ROLES} feature="schedule"><Layout><ScheduleBuilder /></Layout></ProtectedRoute>} />
            <Route path="/requests" element={<ProtectedRoute><Layout><Requests /></Layout></ProtectedRoute>} />
            <Route path="/shift-templates" element={<ProtectedRoute roles={BUILDER_ROLES} feature="template"><Layout><ShiftTemplates /></Layout></ProtectedRoute>} />
            <Route path="/display" element={<ProtectedRoute roles={["admin", "gm", "global_standards", "manager"]}><Layout><DisplayDashboard /></Layout></ProtectedRoute>} />
            <Route path="/display/new" element={<ProtectedRoute roles={["admin", "gm"]}><Layout><DisplayNew /></Layout></ProtectedRoute>} />
            <Route path="/display/:id" element={<ProtectedRoute roles={["admin", "gm", "global_standards", "manager"]}><Layout><DisplayDetail /></Layout></ProtectedRoute>} />
            <Route path="/display/:id/run" element={<ProtectedRoute roles={["admin", "manager"]}><Layout><DisplayRun /></Layout></ProtectedRoute>} />
            <Route path="/display/reports" element={<ProtectedRoute roles={["admin", "gm", "global_standards", "manager"]}><Layout><DisplayReports /></Layout></ProtectedRoute>} />
            <Route path="/display/photos" element={<ProtectedRoute roles={["admin", "gm", "global_standards", "manager"]}><Layout><DisplayPhotos /></Layout></ProtectedRoute>} />
            <Route path="/float" element={<ProtectedRoute roles={["admin", "gm", "manager"]}><Layout><FloatLog /></Layout></ProtectedRoute>} />
            <Route path="/float/new" element={<ProtectedRoute roles={["admin", "gm", "manager"]}><Layout><FloatRecord /></Layout></ProtectedRoute>} />
            <Route path="/float/correction/:correctionOf" element={<ProtectedRoute roles={["admin", "gm", "manager"]}><Layout><FloatRecord /></Layout></ProtectedRoute>} />
            <Route path="/float/:id" element={<ProtectedRoute roles={["admin", "gm", "manager"]}><Layout><FloatDetail /></Layout></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute roles={ADMIN_ROLES}><Layout><Admin /></Layout></ProtectedRoute>} />
            <Route path="/account/password" element={<ProtectedRoute><Layout><ChangePassword /></Layout></ProtectedRoute>} />
            <Route path="/my-display-scores" element={<ProtectedRoute><Layout><MyDisplayScores /></Layout></ProtectedRoute>} />
            <Route path="/my-display-reports" element={<ProtectedRoute><Layout><MyDisplayReports /></Layout></ProtectedRoute>} />
            <Route path="/display/zone-owner-history" element={<ProtectedRoute roles={["admin","gm","manager","assistant_manager","inventory_manager"]}><Layout><ZoneOwnerHistory /></Layout></ProtectedRoute>} />
            <Route path="/receiving" element={<ProtectedRoute roles={["admin", "gm", "manager"]}><Layout><ReceivingList /></Layout></ProtectedRoute>} />
            <Route path="/receiving/:id" element={<ProtectedRoute roles={["admin", "gm", "manager"]}><Layout><ReceivingDetail /></Layout></ProtectedRoute>} />
            <Route path="/receiving/:id/edit" element={<ProtectedRoute roles={["admin", "gm", "manager"]}><Layout><ReceivingNew /></Layout></ProtectedRoute>} />
            <Route path="/damages" element={<ProtectedRoute><Layout><DamageList /></Layout></ProtectedRoute>} />
            <Route path="/damages/new" element={<ProtectedRoute><Layout><DamageNew /></Layout></ProtectedRoute>} />
            <Route path="/damages/:id" element={<ProtectedRoute><Layout><DamageDetail /></Layout></ProtectedRoute>} />
            <Route path="/global-standards" element={<ProtectedRoute roles={["admin", "global_standards"]}><Layout><GlobalStandardsQueue /></Layout></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
